import type { UserProfile, LinkedProvider, ProjectMembership, SSHKey } from '@/types/settings';
import { apiRequest } from '@/lib/api';

const API_BASE = '/api/settings';

// Profile
export async function getProfile(): Promise<{ user: UserProfile }> {
  return apiRequest(`${API_BASE}/profile`);
}

export async function updateProfile(data: { full_name: string }): Promise<{ success: boolean; user?: UserProfile; error?: string }> {
  return apiRequest(`${API_BASE}/profile`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Password
export async function changePassword(data: { password: string; confirm: string }): Promise<{ success: boolean; error?: string }> {
  return apiRequest(`${API_BASE}/password`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// OAuth
export async function getOAuthAccounts(): Promise<{ linked_providers: Record<string, LinkedProvider> }> {
  return apiRequest(`${API_BASE}/oauth`);
}

export async function unlinkOAuth(provider: string): Promise<{ success: boolean; error?: string }> {
  return apiRequest(`${API_BASE}/oauth/${provider}/unlink`, {
    method: 'POST',
  });
}

// 2FA
export async function get2FAStatus(): Promise<{ enabled: boolean }> {
  return apiRequest(`${API_BASE}/2fa/status`);
}

export async function setup2FA(): Promise<{ success: boolean; qr_code?: string; secret_key?: string; error?: string }> {
  return apiRequest(`${API_BASE}/2fa/setup`, {
    method: 'POST',
  });
}

export async function verify2FA(code: string): Promise<{ success: boolean; error?: string }> {
  return apiRequest(`${API_BASE}/2fa/verify`, {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

export async function disable2FA(code: string): Promise<{ success: boolean; error?: string }> {
  return apiRequest(`${API_BASE}/2fa/disable`, {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

// Memberships
export async function getMemberships(): Promise<{ memberships: ProjectMembership[] }> {
  return apiRequest(`${API_BASE}/memberships`);
}

// SSH Keys
export async function getSSHKeys(): Promise<{ ssh_keys: SSHKey[] }> {
  return apiRequest(`${API_BASE}/ssh-keys`);
}

export async function addSSHKey(data: { name: string; public_key: string }): Promise<{ success: boolean; error?: string; message?: string }> {
  return apiRequest(`${API_BASE}/ssh-keys`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteSSHKey(keyId: number): Promise<{ success: boolean; error?: string }> {
  return apiRequest(`${API_BASE}/ssh-keys/${keyId}`, {
    method: 'DELETE',
  });
}

export async function setDefaultSSHKey(keyId: number): Promise<{ success: boolean; error?: string; message?: string }> {
  return apiRequest(`${API_BASE}/ssh-keys/${keyId}/set-default`, {
    method: 'POST',
  });
}

// API Key
export async function getApiKeyStatus(): Promise<{ has_api_key: boolean }> {
  return apiRequest(`${API_BASE}/api-key`);
}

export async function generateApiKey(): Promise<{ success: boolean; api_key?: string; error?: string }> {
  return apiRequest(`${API_BASE}/api-key`, {
    method: 'POST',
    body: JSON.stringify({ confirm: true }),
  });
}

// Billing (user)
export async function getBillingConfig(): Promise<{ plans: string[]; stripe_publishable_key: string | null }> {
  return apiRequest(`${API_BASE}/billing`);
}

// Legal / GDPR
export async function downloadPII(): Promise<{ success: boolean; export?: unknown; error?: string }> {
  return apiRequest(`${API_BASE}/legal/pii_download`, {
    method: 'POST',
  });
}

export async function sendPIIExport(): Promise<{ success: boolean; message?: string; error?: string }> {
  return apiRequest(`${API_BASE}/legal/pii_send_export`, {
    method: 'POST',
  });
}

export async function requestAccountDeletion(): Promise<{ success: boolean; message?: string; error?: string }> {
  return apiRequest(`${API_BASE}/legal/account_deletion`, {
    method: 'POST',
  });
}

// GitHub
export async function getGithubStatus(): Promise<{
  installed: boolean;
  install_url?: string;
  repositories: any[];
  error?: string;
}> {
  return apiRequest(`${API_BASE}/github`);
}
